import Vue from 'vue';
import Router from 'vue-router';
import {
    directHasPermissionRouteGuard,
    hasAnyPermissionRouteGuard,
    hasPermissionRouteGuard,
} from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';
import {
    approvePermissions,
    combinedKnowledgePermissions,
    groupedPMLessonPermissions,
    groupedXLessonPermissions,
    printMultipleWorksheetsPermissions,
    transferExperiencePermissions,
} from '@/helpers/auth/commonPermissions';
import { goToMainLink } from '@/helpers/routing/links';
import { resolveLessonTypeByAppliedLessonType } from '@/helpers/resolveLessonType';
import { appliedLessonTypeFromQuery } from '@/helpers/routing/appliedLessonTypeFromQuery';
import store from '@/vuex/store';

Vue.use(Router);

const router = new Router({
    mode: 'hash',
    base: process.env.BASE_URL,
    routes: [
        // Root
        {
            path: '/home',
            component: () => import('@/components/Home.vue'),
        },

        {
            path: '/',
            name: 'Root',
            component: () => import('@/components/projects/ProjectView.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PROJECT),
        },

        // Main pages
        {
            path: '/projects',
            name: 'Projects',
            component: () => import('@/components/projects/ProjectView.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PROJECT),
        },
        {
            path: '/reports',
            name: 'Reports',
            component: () => import('@/components/reports/Reports.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_REPORT),
        },

        // Experiences
        {
            path: '/experiences',
            name: 'Experiences',
            component: () => import('@/components/experiences/StoreUpdateExperience.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_EXPERIENCE),
        },
        {
            path: '/experiences/transfer',
            name: 'TransferExperience',
            component: () => import('@/components/experiences/TransferExperience.vue'),
            beforeEnter: hasAnyPermissionRouteGuard(transferExperiencePermissions),
        },

        // Projects
        {
            path: '/projects/new',
            name: 'StoreProject',
            component: () => import('@/components/projects/StoreUpdateProject.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PROJECT_CREATE),
        },
        {
            path: '/projects/update',
            name: 'UpdateProject',
            component: () => import('@/components/projects/StoreUpdateProject.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PROJECT_EDIT),
        },

        // Approval
        {
            path: '/approval',
            name: 'ApprovalList',
            component: () => import('@/components/approval/ApprovalList.vue'),
            beforeEnter: hasAnyPermissionRouteGuard(approvePermissions),
        },

        // Notifications
        {
            path: '/notifications',
            name: 'Notifications',
            component: () => import('@/components/notifications/NotificationsPage.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_NOTIFICATION),
        },

        // Authentication
        {
            path: '/auth/login',
            name: 'Login',
            beforeEnter: () => {
                goToMainLink('login');
            },
        },
        {
            path: '/auth/logout',
            name: 'Logout',
            component: () => import('@/components/auth/Logout.vue'),
        },
        {
            path: '/auth/register',
            name: 'Register',
            component: () => import('@/components/auth/Register.vue'),
        },
        {
            path: '/auth/forgotPassword',
            name: 'ForgotPassword',
            component: () => import('@/components/auth/ForgotPassword.vue'),
        },
        {
            path: '/auth/resetPassword',
            name: 'ResetPassword',
            component: () => import('@/components/auth/ResetPassword.vue'),
        },
        {
            path: '/auth/confirmEmail',
            name: 'ConfirmEmail',
            component: () => import('@/components/auth/ConfirmEmail.vue'),
        },

        // User
        {
            path: '/user/profile',
            component: () => import('@/components/user/Profile.vue'),
            children: [
                {
                    path: '/',
                    component: () => import('@/components/user/Overview.vue'),
                },
                {
                    path: '/overview',
                    name: 'AccountOverview',
                    component: () => import('@/components/user/Overview.vue'),
                },
                {
                    path: 'edit',
                    name: 'EditAccount',
                    component: () => import('@/components/user/Edit.vue'),
                    beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_USER_EDIT_OWN),
                },
                {
                    path: 'notifications',
                    name: 'ProfileNotifications',
                    component: () => import('@/components/user/Notifications.vue'),
                },
                {
                    path: 'calendar',
                    name: 'ProfileCalendar',
                    component: () => import('@/components/user/Calendar.vue'),
                },
            ],
        },

        // Knowledge
        {
            path: '/knowledge/combined',
            name: 'CombinedKnowledge',
            component: () => import('@/components/knowledge/CombinedKnowledge.vue'),
            beforeEnter: hasAnyPermissionRouteGuard(combinedKnowledgePermissions),
        },

        // Trades
        {
            path: '/knowledge/trades/new',
            name: 'NewTrade',
            component: () => import('@/components/knowledge/trades/StoreUpdateTrade.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TRADE_CREATE),
        },
        {
            path: '/knowledge/trades/update',
            name: 'UpdateTrade',
            component: () => import('@/components/knowledge/trades/StoreUpdateTrade.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TRADE_EDIT),
        },

        // Topics
        {
            path: '/knowledge/topics',
            name: 'TopicsList',
            component: () => import('@/components/knowledge/topics/TopicsList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TOPIC),
        },
        {
            path: '/knowledge/topics/new',
            name: 'NewTopic',
            component: () => import('@/components/knowledge/topics/StoreUpdateTopic.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TOPIC_CREATE),
        },
        {
            path: '/knowledge/topics/update',
            name: 'UpdateTopic',
            component: () => import('@/components/knowledge/topics/StoreUpdateTopic.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TOPIC_EDIT),
        },
        {
            path: '/knowledge/globalTopics',
            name: 'GlobalTopics',
            component: () => import('@/components/knowledge/topics/GlobalTopics.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TOPIC_GLOBAL),
        },

        // Tags
        {
            path: '/knowledge/tags',
            name: 'TagList',
            component: () => import('@/components/knowledge/tags/TagList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TAG),
        },
        {
            path: '/knowledge/tags/new',
            name: 'NewTag',
            component: () => import('@/components/knowledge/tags/StoreUpdateTag.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TAG_CREATE),
        },
        {
            path: '/knowledge/tags/update',
            name: 'UpdateTag',
            component: () => import('@/components/knowledge/tags/StoreUpdateTag.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_TAG_EDIT),
        },

        // DynamicFields
        {
            path: '/system/dynamicFields',
            name: 'DynamicFieldsList',
            component: () => import('@/components/system/dynamicFields/DynamicFieldsList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_DYNAMIC_FIELD),
        },
        {
            path: '/system/dynamicFields/new',
            name: 'NewDynamicField',
            component: () => import('@/components/system/dynamicFields/StoreUpdateDynamicField.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_DYNAMIC_FIELD_CREATE),
        },
        {
            path: '/system/dynamicFields/update',
            name: 'UpdateDynamicField',
            component: () => import('@/components/system/dynamicFields/StoreUpdateDynamicField.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_DYNAMIC_FIELD_EDIT),
        },

        // Phases
        {
            path: '/knowledge/phases',
            name: 'PhasesList',
            component: () => import('@/components/knowledge/phases/PhasesList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PHASE),
        },
        {
            path: '/knowledge/phases/new',
            name: 'NewPhase',
            component: () => import('@/components/knowledge/phases/StoreUpdatePhase.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PHASE_CREATE),
        },
        {
            path: '/knowledge/phases/update',
            name: 'UpdatePhase',
            component: () => import('@/components/knowledge/phases/StoreUpdatePhase.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PHASE_EDIT),
        },

        // Lessons
        {
            path: '/knowledge/trade/lessons',
            name: 'GroupedLessonsList',
            component: () => import('@/components/knowledge/lessons/GroupedLessonsList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_LESSON),
        },

        // Concepts
        {
            path: '/knowledge/concepts/detailed',
            name: 'DetailedConceptView',
            component: () => import('@/components/knowledge/concepts/DetailedConceptView.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_LESSON),
        },

        // PMPhase
        {
            path: '/knowledge/pmphases/new',
            name: 'NewPMPhase',
            component: () => import('@/components/knowledge/pm/phases/StoreUpdatePMPhase.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_PHASE_CREATE),
        },
        {
            path: '/knowledge/pmphases/update',
            name: 'UpdatePMPhase',
            component: () => import('@/components/knowledge/pm/phases/StoreUpdatePMPhase.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_PHASE_EDIT),
        },

        // PMSubPhase
        {
            path: '/knowledge/pmsubphases/new',
            name: 'NewPMSubPhase',
            component: () => import('@/components/knowledge/pm/subphases/StoreUpdatePMSubPhase.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_SUB_PHASE_CREATE),
        },
        {
            path: '/knowledge/pmsubphases/update',
            name: 'UpdatePMSubPhase',
            component: () => import('@/components/knowledge/pm/subphases/StoreUpdatePMSubPhase.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_SUB_PHASE_EDIT),
        },

        // PMLessons
        {
            path: '/knowledge/pm/lessons/',
            name: 'GroupedPMLessonsList',
            component: () => import('@/components/knowledge/pm/lessons/GroupedPMLessonsList.vue'),
            beforeEnter: hasAnyPermissionRouteGuard(groupedPMLessonPermissions),
        },

        // PMGlobalSubPhases
        {
            path: '/knowledge/globalSubPhases',
            name: 'GlobalSubPhasesContainer',
            component: () => import('@/components/knowledge/pm/subphases/GlobalSubPhasesContainer.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_SUB_PHASE_GLOBAL),
        },
        {
            path: '/knowledge/globalSubPhases/detailed',
            name: 'GlobalSubPhaseLessonsGroupedByPhase',
            component: () => import('@/components/knowledge/globals/GlobalSubPhaseLessonsGroupedByPhase.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_LESSON),
        },

        // Tab X
        // XProcess
        {
            path: '/knowledge/xprocesses/new',
            name: 'NewXProcess',
            component: () => import('@/components/knowledge/x/processes/StoreUpdateXProcess.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_X_PROCESS_CREATE),
        },
        {
            path: '/knowledge/xprocesses/update',
            name: 'UpdateXProcess',
            component: () => import('@/components/knowledge/x/processes/StoreUpdateXProcess.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_X_PROCESS_EDIT),
        },

        // XSubProcess
        {
            path: '/knowledge/xsubprocesses/new',
            name: 'NewXSubProcess',
            component: () => import('@/components/knowledge/x/subprocesses/StoreUpdateXSubProcess.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_X_SUB_PROCESS_CREATE),
        },
        {
            path: '/knowledge/xsubprocesses/update',
            name: 'UpdateXSubProcess',
            component: () => import('@/components/knowledge/x/subprocesses/StoreUpdateXSubProcess.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_X_SUB_PROCESS_EDIT),
        },

        // XLessons
        {
            path: '/knowledge/x/lessons/',
            name: 'GroupedXLessonsList',
            component: () => import('@/components/knowledge/x/lessons/GroupedXLessonsList.vue'),
            beforeEnter: hasAnyPermissionRouteGuard(groupedXLessonPermissions),
        },

        // Knowledge Settings
        {
            path: '/knowledge/settings/',
            name: 'KnowledgeSettings',
            component: () => import('@/components/knowledge/KnowledgeSettings.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_KNOWLEDGE_SETTINGS),
        },

        // Worksheets
        {
            path: '/worksheets/create/trade',
            name: 'CreateWorksheetTrade',
            component: () => import('@/components/worksheets/trade/CreateWorksheetTrade.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_WORKSHEET_CREATE),
        },
        {
            path: '/worksheets/create/pm',
            name: 'CreateWorksheetPM',
            component: () => import('@/components/worksheets/pm/CreateWorksheetPM.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_WORKSHEET_CREATE),
        },
        {
            path: '/worksheets/create/x',
            name: 'CreateWorksheetX',
            component: () => import('@/components/worksheets/x/CreateWorksheetX.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_X_WORKSHEET_CREATE),
        },
        {
            path: '/worksheets/update/trade',
            name: 'UpdateWorksheetTrade',
            component: () => import('@/components/worksheets/trade/UpdateWorksheetTrade.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_WORKSHEET_EDIT),
        },
        {
            path: '/worksheets/update/pm',
            name: 'UpdateWorksheetPM',
            component: () => import('@/components/worksheets/pm/UpdateWorksheetPM.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_WORKSHEET_EDIT),
        },
        {
            path: '/worksheets/update/x',
            name: 'UpdateWorksheetX',
            component: () => import('@/components/worksheets/x/UpdateWorksheetX.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_X_WORKSHEET_EDIT),
        },
        {
            path: '/project/trades/to-dos',
            name: 'TradeToDosList',
            component: () => import('@/components/toDoWizard/trade/TradeToDosList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_WORKSHEET_TODO),
        },
        {
            path: '/project/pm/to-dos',
            name: 'PmToDosList',
            component: () => import('@/components/toDoWizard/pm/PmToDosList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PM_WORKSHEET_TODO),
        },
        {
            path: '/project/x/to-dos',
            name: 'XToDosList',
            component: () => import('@/components/toDoWizard/x/XToDosList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_X_WORKSHEET_TODO),
        },

        // System (Master only)
        {
            path: '/system/companies',
            name: 'CompaniesList',
            component: () => import('@/components/system/companies/CompaniesList.vue'),
        },
        {
            path: '/system/companies/new',
            name: 'NewCompany',
            component: () => import('@/components/system/companies/StoreUpdateCompany.vue'),
        },
        {
            path: '/system/companies/update',
            name: 'UpdateCompany',
            component: () => import('@/components/system/companies/StoreUpdateCompany.vue'),
        },

        {
            path: '/company/update',
            name: 'AdminUpdateCompany',
            component: () => import('@/components/system/AdminUpdateCompany.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_COMPANY_EDIT),
        },

        // manage projects
        {
            path: '/system/company-projects',
            name: 'ProjectsList',
            component: () => import('@/components/system/ProjectsList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_PROJECT_ALL),
        },

        // manage integrations
        {
            path: '/system/integrations',
            name: 'IntegrationsList',
            component: () => import('@/components/system/integrations/IntegrationsList.vue'),
            beforeEnter: hasPermissionRouteGuard(PERMISSIONS.APPEX_INTEGRATIONS),
        },

        {
            path: '/worksheet',
            name: 'WorksheetView',
            component: () => import('@/components/worksheets/WorksheetView.vue'),
            beforeEnter: (to, from, next) =>
                directHasPermissionRouteGuard(
                    next,
                    resolveLessonTypeByAppliedLessonType(appliedLessonTypeFromQuery(to.query), {
                        x: PERMISSIONS.APPEX_X_WORKSHEET,
                        pm: PERMISSIONS.APPEX_PM_WORKSHEET,
                        trade: PERMISSIONS.APPEX_WORKSHEET,
                    })
                ),
        },

        // Export
        {
            path: '/convert/project-applied-lessons',
            name: 'convert.project-applied-lessons',
            meta: {
                print: true,
            },
            component: () => import('@/components/export/ExportProjectAppliedLessons1Click.vue'),
            beforeEnter: hasPermissionRouteGuard(printMultipleWorksheetsPermissions),
        },
        {
            path: '/convert/worksheet-applied-lessons',
            name: 'convert.worksheet-applied-lessons',
            meta: {
                print: true,
            },
            component: () => import('@/components/export/ExportWorksheetAppliedLessons.vue'),
            beforeEnter: (to, from, next) =>
                directHasPermissionRouteGuard(
                    next,
                    resolveLessonTypeByAppliedLessonType(appliedLessonTypeFromQuery(to.query), {
                        x: [PERMISSIONS.APPEX_X_WORKSHEET_PRINT],
                        pm: [PERMISSIONS.APPEX_PM_WORKSHEET_PRINT],
                        trade: [PERMISSIONS.APPEX_WORKSHEET_PRINT],
                    })
                ),
        },
    ],
});

const waitForStorageToBeReady = async (to, from, next) => {
    // @ts-ignore restored property is added by vuex-persist
    await store.restored;
    next();
};
router.beforeEach(waitForStorageToBeReady);

export default router;
